import styled from '@emotion/styled'
import {
    INLINE_CTA_ACTION,
    toApiFullLandingPageLink,
} from '@storefronts/common-types'
import { useCallback, useContext } from 'react'
import { AnalyticsContext } from '../../../../context/analyticsContext'
import { HLSContext } from '../../../../context/hlsContext'
import { PRIMARY_PAGE_FILL_COLOR_VAR } from '../../../../context/CustomizationContextProvider'
import { ViddyLPContext } from '../../../../context/viddyLPContext'
import { VideoContext } from '../../../../context/viddyVideoContext'
import { useMinWidth } from '../../../../hooks/useMinWidth'
import {
    HEADER_HEIGHT_VAR,
    ScrollingLayout,
    SECTION_CONTAINER_NAME,
} from '../../../../style/scrollingLayout'
import { onHover } from '../../../../style/interactiveStates'
import { Constants } from '../../../../style/styleConstants'
import { useCombinedProductHistoryAndLandingPageLinks } from '../../../../utils/useCombinedProductHistory'
import { videoToPlaylistPlayerVideo } from '../../../../utils/helpers'
import { MutedSpeakerIcon, UnmutedSpeakerIcon } from '../../../icons/Icons'
import { VideoPlayer } from '../../../shared/VideoPlayer'
import { BottomControlsSingleVideo } from '../../bottom-controls/BottomControlsSingleVideo'
import { InlineProductPageContent } from '../../full-screen/ProductDrawer'
import { PlayButtonOverlay } from '../../PulsePlayButton'

/**
 * Shoppable video hero.
 *
 * Below the breakpoint the video is sticky at full height, the product overlay
 * sits on top of it, and content scrolls over it. Above the breakpoint the
 * video keeps its size but stops being sticky, and the product moves out of
 * the overlay into a panel beside it with its own variant picker and
 * add-to-cart.
 */

const HERO_TOP_GAP_PX = 16

const HeroRow = styled.div({
    width: '100%',
    [ScrollingLayout.DESKTOP_MEDIA]: {
        display: 'flex',
        // Both columns start at the row's top edge. The panel is flush for the
        // same reason: its own leading margin would drop the title below the
        // video it is meant to sit level with.
        alignItems: 'flex-start',
        // Centres the video when there is no product to sit beside it.
        justifyContent: 'center',
        gap: 32,
        marginTop: HERO_TOP_GAP_PX,
    },
})

const VideoColumn = styled.div({
    width: '100%',
    [ScrollingLayout.DESKTOP_MEDIA]: {
        width: 'auto',
        flex: '0 0 auto',
        // Holds the video in view while the panel beside it scrolls, and lets
        // go when the hero ends. The containing block is the hero row, so the
        // release point is exactly where the first section begins.
        position: 'sticky',
        alignSelf: 'flex-start',
        top: `calc(var(${HEADER_HEIGHT_VAR}, 0px) + ${ScrollingLayout.HERO_STICKY_GAP_PX}px)`,
    },
})

const VideoFrame = styled.div({
    position: 'relative',
    overflow: 'hidden',
    // VideoPlayer gives the <video> z-index: -1. Without a stacking context
    // here that negative index escapes upward and the video paints behind this
    // background instead of on it, leaving a black rectangle.
    isolation: 'isolate',
    backgroundColor: 'black',
    width: '100%',
    height: '100dvh',
    [ScrollingLayout.DESKTOP_MEDIA]: {
        width: 'auto',
        height: 'min(70vh, 640px)',
        aspectRatio: '9 / 16',
        borderRadius: 12,
    },
})

/** Matches PIPFullScreenOverlayContainer: children opt back into pointers. */
const VideoOverlay = styled.div({
    position: 'absolute',
    inset: 0,
    zIndex: 2,
    display: 'flex',
    flexDirection: 'column',
    pointerEvents: 'none',
    borderRadius: 'inherit',
})

const TapPanel = styled.div({
    height: '100%',
    width: '100%',
    display: 'flex',
    cursor: 'pointer',
    justifyContent: 'center',
    alignItems: 'center',
})

/** The overlay only fires analytics on interaction, so hiding it is enough. */
const OverlayProduct = styled.div({
    display: 'contents',
    [ScrollingLayout.DESKTOP_MEDIA]: {
        display: 'none',
    },
})

const MuteButton = styled.button({
    display: 'none',
    [ScrollingLayout.DESKTOP_MEDIA]: {
        position: 'absolute',
        top: 12,
        right: 12,
        zIndex: 3,
        width: 32,
        height: 32,
        borderRadius: '50%',
        border: 'none',
        padding: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        ...onHover({ backgroundColor: 'rgba(0,0,0,0.78)' }),
    },
})

/**
 * The product writes its layout against the section container, which every
 * other place it appears provides. The hero sits outside the sections list, so
 * without this the product falls back to its narrowest layout here however
 * wide the panel is.
 */
const ProductPanel = styled.div({
    display: 'none',
    [ScrollingLayout.DESKTOP_MEDIA]: {
        display: 'block',
        flex: '1 1 auto',
        minWidth: 0,
        containerType: 'inline-size',
        containerName: SECTION_CONTAINER_NAME,
        // The column is reserved by CSS during server render so the video does
        // not move when the panel mounts after hydration.
        backgroundColor: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
    },
})

export const HeroVideoBlock: React.FC = () => {
    const { landingPage } = useContext(ViddyLPContext)
    const { setVideoElementRef } = useContext(HLSContext)
    const { isLPM, isMuted, setIsMuted, setVideoTapped } =
        useContext(VideoContext)
    const { Analytics } = useContext(AnalyticsContext)
    const productHistoryLinks =
        useCombinedProductHistoryAndLandingPageLinks(true)
    const isDesktop = useMinWidth(Constants.EXPANDED_BREAKPOINT)

    const registerVideoElement = useCallback(
        (videoRef: React.RefObject<HTMLVideoElement>) => {
            setVideoElementRef?.(0, 0, videoRef)
        },
        [setVideoElementRef],
    )

    const toggleMute = () => {
        const nextMuted = !isMuted
        setIsMuted(nextMuted)
        setVideoTapped(true)
        Analytics.TRACK_VIDEO_PLAYER_MUTE_TOGGLE(
            nextMuted ? 'muted' : 'unmuted',
            'main_video_icon_click',
        )
    }

    if (landingPage.ux !== 'JUST_CONTENT_NO_OVERLAY') return null

    const playlist = landingPage.defaultPlaylist
    const video = playlist?.videos?.[0]
    if (!playlist || !video) return null

    const playerVideo = videoToPlaylistPlayerVideo(video, playlist)
    if (!playerVideo) return null

    const lpLink = landingPage.landingPageLinks[0] ?? productHistoryLinks?.[0]
    const fullLink = toApiFullLandingPageLink(lpLink)

    return (
        <HeroRow>
            <VideoColumn>
                <VideoFrame
                    onClick={() => {
                        Analytics.TRACK_LANDING_PAGE_VIDEO_CONTAINER_CLICK()
                    }}
                >
                    <VideoPlayer
                        isActive
                        playerVideo={playerVideo}
                        setVideoElementRef={registerVideoElement}
                    />
                    <VideoOverlay>
                        <TapPanel>{isLPM && <PlayButtonOverlay />}</TapPanel>
                        <OverlayProduct>
                            <BottomControlsSingleVideo lpLink={lpLink} />
                        </OverlayProduct>
                    </VideoOverlay>
                    <MuteButton
                        type="button"
                        onClick={(event) => {
                            event.stopPropagation()
                            toggleMute()
                        }}
                        aria-label={isMuted ? 'Unmute' : 'Mute'}
                    >
                        {isMuted ? (
                            <MutedSpeakerIcon />
                        ) : (
                            <UnmutedSpeakerIcon />
                        )}
                    </MuteButton>
                </VideoFrame>
            </VideoColumn>

            {/*
             * The column is present from the server render so the video does
             * not move when its contents arrive, but the contents themselves
             * wait for the breakpoint: InlineProductPageContent fires
             * product-view pixels on mount, so rendering it hidden on a phone
             * would report views the overlay never reports.
             */}
            {fullLink && (
                <ProductPanel>
                    {isDesktop && (
                        <InlineProductPageContent
                            lpLink={fullLink}
                            inlineCTAActions={[INLINE_CTA_ACTION.ADD_TO_CART]}
                            flushContent
                            singleColumn
                        />
                    )}
                </ProductPanel>
            )}
        </HeroRow>
    )
}
